/**
 * Vehicle Fields - Single source of truth
 * Enums, labels, badges, and form options for vehicle data
 */
import { ShieldCheck, History, Ban } from 'lucide-react';

// ─── Fuel Types (canonical lowercase keys — matches backend reference data) ──
export const FUEL_TYPES = [
  { value: 'essence', label: 'Essence' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'electrique', label: 'Électrique' },
  { value: 'hybride', label: 'Hybride' },
  { value: 'hybride_rechargeable', label: 'Hybride rechargeable' },
  { value: 'lpg', label: 'LPG' },
  { value: 'cng', label: 'CNG' },
];

// ─── Fuel Filter Pills (same canonical keys as FUEL_TYPES) ──────────
export const FUEL_FILTER_PILLS = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'essence', label: 'Essence' },
  { value: 'hybride', label: 'Hybride' },
  { value: 'electrique', label: 'Électrique' },
];

// ─── Transmission ────────────────────────────────────────────────────
export const TRANSMISSION = {
  AUTOMATIC: 'automatic',
  MANUAL: 'manual',
  SEMI_AUTOMATIC: 'semi-automatic',
};

export const TRANSMISSION_CONFIG = {
  [TRANSMISSION.AUTOMATIC]: { label: 'Automatique', labelShort: 'Auto' },
  [TRANSMISSION.MANUAL]: { label: 'Manuelle', labelShort: 'Manuel' },
  [TRANSMISSION.SEMI_AUTOMATIC]: { label: 'Semi-automatique', labelShort: 'Semi-auto' },
};

export const TRANSMISSION_OPTIONS = Object.entries(TRANSMISSION_CONFIG).map(([value, config]) => ({
  value,
  label: config.label,
}));

export const TRANSMISSION_FILTER_PILLS = [
  { value: TRANSMISSION.AUTOMATIC, label: 'Auto' },
  { value: TRANSMISSION.MANUAL, label: 'Manuelle' },
];

export const getTransmissionLabel = (value, format = 'short') => {
  const config = TRANSMISSION_CONFIG[value];
  if (!config) return value || '';
  return format === 'full' ? config.label : config.labelShort;
};

// ─── Body Types (canonical lowercase keys) ──────────────────────────
export const BODY_TYPES = [
  { value: 'berline', label: 'Berline' },
  { value: 'break', label: 'Break' },
  { value: 'suv', label: 'SUV / Crossover' },
  { value: 'coupe', label: 'Coupé' },
  { value: 'cabriolet', label: 'Cabriolet' },
  { value: 'monospace', label: 'Monospace' },
  { value: 'utilitaire', label: 'Utilitaire' },
  { value: 'citadine', label: 'Citadine' },
  { value: 'pick-up', label: 'Pick-up' },
];

// ─── Colors (canonical lowercase keys) ───────────────────────────────
export const COLORS = [
  { value: 'noir', label: 'Noir' },
  { value: 'blanc', label: 'Blanc' },
  { value: 'gris', label: 'Gris' },
  { value: 'argent', label: 'Argent' },
  { value: 'bleu', label: 'Bleu' },
  { value: 'rouge', label: 'Rouge' },
  { value: 'vert', label: 'Vert' },
  { value: 'beige', label: 'Beige' },
  { value: 'marron', label: 'Marron' },
  { value: 'orange', label: 'Orange' },
  { value: 'jaune', label: 'Jaune' },
  { value: 'bronze', label: 'Bronze' },
  { value: 'violet', label: 'Violet' },
  { value: 'bordeaux', label: 'Bordeaux' },
  { value: 'or', label: 'Or' },
];

// ─── Interior Types (canonical lowercase keys) ──────────────────────
export const INTERIOR_TYPES = [
  { value: 'cuir', label: 'Cuir' },
  { value: 'tissu', label: 'Tissu' },
  { value: 'alcantara', label: 'Alcantara' },
  { value: 'semi-cuir', label: 'Semi-cuir' },
  { value: 'autre', label: 'Autre' },
];

// ─── Paint Types (canonical lowercase keys) ─────────────────────────
export const PAINT_TYPES = [
  { value: 'metallisee', label: 'Métallisée' },
  { value: 'nacree', label: 'Nacrée' },
  { value: 'mate', label: 'Mate' },
  { value: 'unie', label: 'Unie' },
];

// ─── Drive Types (canonical lowercase keys) ─────────────────────────
export const DRIVE_TYPES = [
  { value: 'traction', label: 'Traction avant' },
  { value: 'propulsion', label: 'Propulsion' },
  { value: '4x4', label: '4x4 / Intégrale' },
];

// ─── Euro Standards (canonical lowercase keys) ──────────────────────
export const EURO_STANDARDS = [
  { value: 'euro-6d', label: 'Euro 6d' },
  { value: 'euro-6d-temp', label: 'Euro 6d-TEMP' },
  { value: 'euro-6c', label: 'Euro 6c' },
  { value: 'euro-6b', label: 'Euro 6b' },
  { value: 'euro-6', label: 'Euro 6' },
  { value: 'euro-5', label: 'Euro 5' },
  { value: 'euro-4', label: 'Euro 4' },
];

// ─── Label lookup helpers ────────────────────────────────────────────
// Convert canonical key to display label (e.g., "diesel" → "Diesel", "coupe" → "Coupé")
const _buildLabelMap = (items) => {
  const map = {};
  items.forEach(({ value, label }) => { map[value] = label; });
  return map;
};

const _fuelLabelMap = _buildLabelMap(FUEL_TYPES);
const _bodyLabelMap = _buildLabelMap(BODY_TYPES);
const _driveLabelMap = _buildLabelMap(DRIVE_TYPES);
const _interiorLabelMap = _buildLabelMap(INTERIOR_TYPES);
const _paintLabelMap = _buildLabelMap(PAINT_TYPES);
const _euroLabelMap = _buildLabelMap(EURO_STANDARDS);

export const getFuelTypeLabel = (value) => _fuelLabelMap[value] || value || '';
export const getBodyTypeLabel = (value) => _bodyLabelMap[value] || value || '';
export const getDriveTypeLabel = (value) => _driveLabelMap[value] || value || '';
export const getInteriorTypeLabel = (value) => _interiorLabelMap[value] || value || '';
export const getPaintTypeLabel = (value) => _paintLabelMap[value] || value || '';
export const getEuroStandardLabel = (value) => _euroLabelMap[value] || value || '';

// ─── Condition Badges ────────────────────────────────────────────────
export const CONDITION_BADGES = [
  {
    test: (v) => v.had_accident === false,
    label: 'Sans accident',
    icon: ShieldCheck,
    positive: true,
  },
  {
    test: (v) => !!v.has_full_service_history,
    label: 'Historique complet',
    icon: History,
    positive: true,
  },
  {
    test: (v) => !!v.non_smoking,
    label: 'Non fumeur',
    icon: Ban,
    positive: true,
  },
];

export const getConditionBadges = (vehicle) =>
  CONDITION_BADGES.filter((badge) => badge.test(vehicle));

// ─── Days (French) ───────────────────────────────────────────────────
export const DAYS_FR = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche',
};
